import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import { Grid, Button } from '@material-ui/core';
import { paymentType } from '@ourbranch/lookups';
import { track } from 'core/helpers/analytics';

import { useBasisTheoryToken } from 'common/hooks/use-basis-theory-token';
import useSession from 'core/hooks/use-session';
import { LabelTooltip } from 'core/components/label-tooltip';
import { FormField } from 'core/components/form';
import { useToast } from 'core/components/toast';
import { useStore } from 'core/store';
import { ACHFields } from 'common/components/payment/ach-fields';
import { useDisabledState } from 'common/disabled-context/context';
import { SecondaryPaymentMethod } from '../secondary-payment-method';
import { StripeCustomerIdField } from '../stripe-customer-id-field';
import useStyles from '../billing-details.styles';

const BankAccounts = () => {
  const classes = useStyles();
  const toast = useToast();
  const { disabled } = useDisabledState();
  const { user, viewOnly } = useSession();
  const {
    account: {
      id: accountId,
      policies: {
        policy: {
          policy: { id },
          billingDetails,
          addBankAccount,
          stripeCustomerId
        }
      }
    }
  } = useStore();
  const { setFieldValue, values, setFieldTouched } = useFormikContext();
  const { getBasisTheoryACHToken } = useBasisTheoryToken();

  const allPaymentMethods = billingDetails.allPaymentMethods || [];
  const bankAccounts = allPaymentMethods
    .filter((method) => method.id?.startsWith('ba') || method.basisTheoryBankAccountToken)
    .map((account) => ({
      id: account.id,
      value: account.last4.slice(-4).padStart(account.last4.length * 1.5, '*')
    }));

  const [showAccountFields, setShowAccountFields] = useState(false);

  useEffect(() => {
    if (values.paymentType === paymentType.Escrow) {
      setFieldValue('paymentType', paymentType.OneTime);
    }
  });

  const toggleAccountFields = () => {
    setShowAccountFields((prev) => !prev);
  };

  const addAccount = async () => {
    try {
      const basisTheoryBankAccountTokenInfo = await getBasisTheoryACHToken({
        routingNumberTextElementId: 'routingNumber',
        accountNumberTextElementId: 'accountNumber',
        accountHolderNameTextElementId: 'accountHolderName'
      });

      const last4 = basisTheoryBankAccountTokenInfo.data.account_number.slice(-4); // "XXXXX2023" -> "2023"
      const basisTheoryBankAccountToken = basisTheoryBankAccountTokenInfo.id;

      // check if existing bank account
      const bankAccountExists = billingDetails?.allPaymentMethods?.find(
        (existingPayment) => last4.endsWith(existingPayment.last4) && existingPayment.id.startsWith('ba')
      );
      if (bankAccountExists) {
        throw new Error(`Bank account ending in ${last4} already exists`);
      }

      const accountData = {
        id: basisTheoryBankAccountTokenInfo.id,
        basisTheoryBankAccountToken,
        routingNumber: basisTheoryBankAccountTokenInfo.data.routing_number,
        last4
      };

      track('Staff - Changed Payment Method', {
        oldPaymentMethod: billingDetails?.activePaymentMethod?.bankName ? 'ACH' : 'CC',
        newPaymentMethod: 'ACH',
        last4,
        policyId: id,
        customerId: accountId,
        staffUser: user.username
      });
      setFieldValue('defaultBankAccount', accountData);
      addBankAccount(accountData);
      setFieldTouched('stripeCustomerId', values.stripeCustomerId !== stripeCustomerId);
      setFieldValue('stripeCustomerId', values.stripeCustomerId);
      setShowAccountFields(false);
    } catch (error) {
      await toast.notify({
        type: 'error',
        message: error.message
      });
    }
  };

  return (
    <>
      <Grid container key="account-selected" justifyContent="flex-start" className={classes.bankAccounts} spacing={4}>
        <Grid item xs={4}>
          <LabelTooltip
            label="Primary Account Number"
            tooltip={{
              label: 'More Info',
              onHoverText: 'This is the primary bank account we’ll use to process payments for this policy.'
            }}
          >
            <FormField
              name="defaultBankAccount.id"
              type="select"
              mode="dark"
              options={bankAccounts}
              permissions={{ isLicensedAction: false }}
              fast={false}
            />
          </LabelTooltip>
        </Grid>

        <StripeCustomerIdField />
      </Grid>
      <Grid container className={classes.bankAccounts} spacing={4}>
        {!showAccountFields && (
          <Button xs={4} disabled={disabled} onClick={toggleAccountFields} variant="text" color="secondary">
            Add new account
          </Button>
        )}
      </Grid>
      {showAccountFields && (
        <>
          <Grid container item key="account-info" justifyContent="flex-start" alignItems="center">
            <ACHFields mode="dark" />
          </Grid>
          <Grid container item xs={12}>
            <Button variant="contained" color="secondary" type="submit" onClick={addAccount}>
              Add account
            </Button>
            <Button onClick={() => toggleAccountFields(false)} variant="text" color="secondary">
              Close
            </Button>
          </Grid>
        </>
      )}
      {!showAccountFields && values.defaultBankAccount?.accountHolder && (
        <Grid container className={classes.bankAccounts} spacing={4}>
          <FormField
            name="defaultBankAccount.accountHolder"
            type="value"
            label="Account Holder"
            mode="dark"
            xs={4}
            value={values.defaultBankAccount.accountHolder}
          />
          <FormField
            name="defaultBankAccount.routingNumber"
            type="value"
            label="Routing Number"
            xs={4}
            mode="dark"
            value={values.defaultBankAccount.routingNumber}
          />
        </Grid>
      )}
      <Grid container className={classes.bankAccounts} spacing={4}>
        {!viewOnly && <SecondaryPaymentMethod />}
      </Grid>
    </>
  );
};

export default observer(BankAccounts);
